import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import {
  REACTION_CHEM_SEARCH_DESCRIPTION,
  REACTION_CHEM_SEARCH_MODAL_TITLE,
} from 'chemistry/ChemistryConstants';
import ChemSearchModalFrame from 'chemistry/ChemSearchModalFrame';
import {
  ChemSearchMoleculeOptions,
  ChemSearchTypeToggle,
  ReactionRoleSelector,
} from 'chemistry/ChemSearchModalOptions';
import {chemSearchModalStyles} from 'chemistry/ChemSearchModalStyles';
import {buildReactionChemSearchSubmitPayload, ChemSearchSubmit} from 'chemistry/chemSearchSubmit';
import {
  getDefaultChemSearchOperator,
  getDefaultMinSimilarity,
  ToggleHighlightParams,
} from 'chemistry/ChemSearchUtils';
import {
  ChemicalStructureDrawer,
  ChemicalStructureDrawerMode,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import ReactionMoleculeTabs from 'chemistry/ReactionMoleculeTabs';
import {useChemSearchModalSubmit} from 'chemistry/useChemSearchModalSubmit';
import {useReactionChemSearchModalState} from 'chemistry/useReactionChemSearchModalState';
import {ChemicalStructureSearchOperator, SearchParams} from 'search/SearchConstants';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import {GRAY_LIGHT, GRAY_SEMIDARK} from 'ui-library/Colors';

export interface ReactionChemSearchModalProps {
  initialMol: string | null;
  onRequestSubmit: (submit: ChemSearchSubmit) => void;
  onRequestClose: () => void;
  toggleHighlightParams: ToggleHighlightParams;
  searchParams: SearchParams;
}

export default function ReactionChemSearchModal({
  initialMol,
  onRequestSubmit,
  onRequestClose,
  toggleHighlightParams,
  searchParams,
}: ReactionChemSearchModalProps): JSX.Element {
  const defaultMinSimilarity = getDefaultMinSimilarity(searchParams);
  const [highlightChecked, setHighlightChecked] = useState<boolean>(
    toggleHighlightParams.shouldHighlightSubstructure
  );
  const [chemSearchOperator, setChemSearchOperator] = useState<ChemicalStructureSearchOperator>(() =>
    getDefaultChemSearchOperator(searchParams)
  );

  const {clauses, activeClauseId, activeClause, selectTab, updateClause, addClause, removeClause} =
    useReactionChemSearchModalState({
      searchParams,
      initialMol,
      defaultMinSimilarity,
    });

  const buildSubmitResult = useCallback(
    () =>
      buildReactionChemSearchSubmitPayload({
        clauses,
        activeClauseId,
        chemSearchOperator,
      }),
    [activeClauseId, chemSearchOperator, clauses]
  );

  const {submitError, clearSubmitError, handleSubmit} = useChemSearchModalSubmit({
    toggleHighlightParams,
    highlightChecked,
    chemSearchOperator,
    onRequestSubmit,
    buildSubmitResult,
    onValidationError: (result) => {
      if (result.focusClauseId != null) {
        selectTab(result.focusClauseId);
      }
    },
  });

  return (
    <ChemSearchModalFrame
      title={REACTION_CHEM_SEARCH_MODAL_TITLE}
      showHeaderBorder={false}
      submitError={submitError}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
    >
      <div className={css(styles.reactionSearchIntro)}>
        <p className={css(styles.reactionSearchDescription)}>{REACTION_CHEM_SEARCH_DESCRIPTION}</p>
      </div>
      <div className={css(styles.reactionSearchTypeToggleContainer)}>
        <ChemSearchTypeToggle
          chemSearchOperator={chemSearchOperator}
          setChemSearchOperator={(operator) => {
            clearSubmitError();
            setChemSearchOperator(operator);
          }}
          className={css(styles.reactionSearchTypeToggle)}
        />
      </div>
      <ReactionMoleculeTabs
        activeClauseId={activeClauseId}
        clauses={clauses}
        onAddClause={addClause}
        onRemoveClause={removeClause}
        onSelectTab={selectTab}
      />
      <div
        aria-labelledby={`reaction_molecule_${activeClauseId}_TAB`}
        className={css(chemSearchModalStyles.chemicalDrawer)}
        id={`reaction_molecule_${activeClauseId}_PANEL`}
        role="tabpanel"
      >
        <ChemicalStructureDrawer
          key={activeClauseId}
          onMoleculeChange={(chemicalStructureQuery) => {
            clearSubmitError();
            updateClause(activeClauseId, {chemicalStructureQuery});
          }}
          initialValue={activeClause.chemicalStructureQuery}
          mode={ChemicalStructureDrawerMode.STANDARD}
        />
      </div>
      <div className={css(chemSearchModalStyles.searchOptionsContainer)}>
        <ReactionRoleSelector
          reactionComponentRole={activeClause.reactionComponentRole}
          setReactionComponentRole={(reactionComponentRole) => {
            clearSubmitError();
            updateClause(activeClauseId, {reactionComponentRole});
          }}
          className={css(chemSearchModalStyles.searchOptions)}
        />
        <ChemSearchMoleculeOptions
          highlightChecked={highlightChecked}
          setHighlightChecked={setHighlightChecked}
          chemSearchOperator={chemSearchOperator}
          minSimilarity={activeClause.minSimilarity}
          setMinSimilarity={(reactionMinSimilarity) => {
            clearSubmitError();
            updateClause(activeClauseId, {minSimilarity: reactionMinSimilarity});
          }}
          toggleHighlightParams={toggleHighlightParams}
          className={css(chemSearchModalStyles.searchOptions)}
        />
      </div>
    </ChemSearchModalFrame>
  );
}

const styles = StyleSheet.create({
  reactionSearchIntro: {
    padding: '0 16px 16px',
  },
  reactionSearchTypeToggleContainer: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    padding: '0 16px 0px',
  },
  reactionSearchTypeToggle: {
    paddingTop: 0,
    borderTop: 0,
  },
  reactionSearchDescription: {
    ...FONT_PROXIMA_12,
    color: GRAY_SEMIDARK,
    margin: 0,
  },
});
